import { signal } from '@preact/signals';
import { Icon } from './Icon';

export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'parwaaz.theme';

function readStored(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // ignore
  }
  // Default to light, not "system" — an officer's console (and a visiting pilot's phone) should
  // open the same way every time regardless of the device's OS theme; anyone who wants dark or
  // to follow the system can still choose it here, and that choice persists as before.
  return 'light';
}

export const theme = signal<ThemeMode>(readStored());

function applyTheme(mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  if (mode === 'system') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = mode;
}

applyTheme(theme.value);

export function setTheme(mode: ThemeMode) {
  theme.value = mode;
  applyTheme(mode);
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore — theme just won't persist across reloads
  }
}

const OPTIONS: { mode: ThemeMode; label: string; icon: 'sun' | 'moon' | 'globe' }[] = [
  { mode: 'light', label: 'Light', icon: 'sun' },
  { mode: 'dark', label: 'Dark', icon: 'moon' },
  { mode: 'system', label: 'System', icon: 'globe' },
];

/** system / light / dark — sets `data-theme` on <html>, which tokens.css keys its dark palette to. */
export function ThemeSwitch() {
  return (
    <div class="c-theme-switch" role="group" aria-label="Theme">
      {OPTIONS.map((opt) => (
        <button
          key={opt.mode}
          type="button"
          class="c-theme-switch__opt"
          aria-pressed={theme.value === opt.mode}
          onClick={() => setTheme(opt.mode)}
          title={opt.label}
        >
          <Icon name={opt.icon} size={14} title={opt.label} />
        </button>
      ))}
    </div>
  );
}
